"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { fetchWithAuth } from "@/lib/auth";
import { formatEventDate } from "@/lib/eventTime";
import { Archive, ArrowRight, BookOpen, Calendar, ChevronRight, FileText, Settings, Users, Activity, ListChecks } from "lucide-react";
import { InterestSubmissionsView } from "@/components/dashboard/InterestSubmissionsView";
import { TourButton } from "@/components/tours/TourButton";
import { useAutoTour } from "@/components/tours/useAutoTour";
import { TOURS } from "@/components/tours/catalog";

interface KpiData {
    totalVolunteers: number;
}

interface RecentActivity {
    id: string;
    title: string;
    status: string;
    entity_type: string;
    entity_name?: string;
    start_at?: string;
    // The event's own zone. Without it the date below renders on the VIEWER's
    // calendar, which is the same defect the Event Logistics card had.
    meeting_timezone?: string;
}

interface MyGroup {
    entity_type: string;
    entity_id: number;
    name?: string;
    entity_name?: string;
    role?: string;
    status?: string;
}

interface UserProfile {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    is_staff?: boolean;
    member_since: string;
    member_status: string;
    profile_photo_url?: string;
    bio?: string;
    linkedin?: string;
    phone?: string;
    job_title?: string;
    company?: string;
    city?: string;
    country?: string;
    program_journey?: string;
    membership_journey?: string;
    designations?: string;
    assignments?: { assignment_id: string; role: string; entity_type: string; entity_name: string }[];
}

const STATUS_COLOR: Record<string, string> = {
    approved: "bg-emerald-100 text-emerald-700",
    completed: "bg-[#003d45]/10 text-[#003d45]",
    pending_review: "bg-amber-100 text-amber-700",
    pending_staff_review: "bg-amber-100 text-amber-700",
    draft: "bg-slate-100 text-slate-500",
};

const TYPE_COLOR: Record<string, string> = {
    committee: "bg-[#003d45] text-white",
    chapter: "bg-[#007a8a] text-white",
    community: "bg-[#ae232d] text-white",
};


function groupUrl(type: string, id: number) {
    const plural = type === "community" ? "communities" : type === "committee" ? "committees" : "chapters";
    return `/groups/${plural}/${id}`;
}

export function OverviewPanel() {
    const [kpi, setKpi] = useState<KpiData>({ totalVolunteers: 0 });
    const [recent, setRecent] = useState<RecentActivity[]>([]);
    const [myGroups, setMyGroups] = useState<MyGroup[]>([]);
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const { replay: replayTour } = useAutoTour("staff.overview", TOURS["staff.overview"]);

    useEffect(() => {
        async function load() {
            try {
                const [assignRes, groupRes, meetRes, myGroupsRes, meRes] = await Promise.all([
                    fetch("/api/v1/organizations/assignments?limit=2000"),
                    fetchWithAuth("/api/v1/groups/discover"),
                    fetchWithAuth("/api/v1/meetings"),
                    fetchWithAuth("/api/v1/groups/my-groups"),
                    fetchWithAuth("/api/v1/auth/me"),
                ]);

                let volunteers = 0, groups = 0;
                if (assignRes.ok) {
                    const data = await assignRes.json();
                    const ids = new Set((data as { user_id: string }[]).map((a) => a.user_id));
                    volunteers = ids.size;
                }
                if (groupRes.ok) {
                    const data = await groupRes.json();
                    groups = Array.isArray(data) ? data.length : 0;
                }
                if (myGroupsRes.ok) {
                    const data = await myGroupsRes.json();
                    setMyGroups(Array.isArray(data) ? data : []);
                }

                let pending = 0, upcoming = 0, total = 0, recentItems: RecentActivity[] = [];
                if (meetRes.ok) {
                    const meetings: RecentActivity[] = await meetRes.json();
                    total = meetings.length;
                    pending = meetings.filter(m => ["pending_review", "pending_staff_review"].includes(m.status)).length;
                    const now = new Date();
                    upcoming = meetings.filter(m => m.start_at && new Date(m.start_at) > now && m.status === "approved").length;
                    const seen = new Set<string>();
                    recentItems = meetings
                        .filter(m => { if (seen.has(m.id)) return false; seen.add(m.id); return true; })
                        .sort((a, b) => (b.start_at ? new Date(b.start_at).getTime() : 0) - (a.start_at ? new Date(a.start_at).getTime() : 0))
                        .slice(0, 6);
                }

                if (meRes.ok) {
                    const data = await meRes.json();
                    setProfile(data);
                }

                setKpi({ totalVolunteers: volunteers });
                setRecent(recentItems);
            } finally {
                setLoading(false);
            }
        }
        load();
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-[#e2e8f0] border-t-[#003d45] rounded-full animate-spin" />
            </div>
        );
    }

    const statusIsActive = (profile?.member_status || "").toLowerCase() === "active";

    const isAuthorizedForInterest = 
        ["admin", "superadmin", "staff"].includes((profile?.role || "").toLowerCase()) ||
        !!profile?.is_staff;

    return (
        <div className="space-y-8">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-black text-[#003d45] mb-1">My Dashboard</h2>
                    <p className="text-sm text-[#666666]">Overview of your active groups and recent activities.</p>
                </div>
                <TourButton onClick={replayTour} />
            </div>


            {/* Profile Section */}
            {profile && (
                <div id="staff-overview-profile" className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm overflow-hidden">
                    {/* Header band */}
                    <div className="h-2 bg-gradient-to-r from-[#003d45] to-[#007a8a]" />
                    <div className="p-6">
                        <div className="flex flex-col sm:flex-row gap-5">
                            {/* Avatar */}
                            <div className="flex-shrink-0">
                                {profile.profile_photo_url ? (
                                    <img src={profile.profile_photo_url} alt="Profile" className="w-20 h-20 rounded-2xl object-cover border-2 border-[#e2e8f0]" />
                                ) : (
                                    <div className="w-20 h-20 rounded-2xl bg-[#003d45] flex items-center justify-center text-white font-black text-2xl border-2 border-[#003d45]">
                                        {(profile.first_name?.[0] || "")}{(profile.last_name?.[0] || "")}
                                    </div>
                                )}
                            </div>

                            {/* Core info */}
                            <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                    <h3 className="text-xl font-black text-[#003d45] leading-tight">{profile.first_name} {profile.last_name}</h3>
                                    <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded-full ${statusIsActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                                        {statusIsActive ? "Active" : profile.member_status || "Inactive"}
                                    </span>
                                </div>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2">
                                    <span className="text-xs font-bold text-[#007a8a] uppercase tracking-wider">{profile.role}</span>
                                    {profile.designations && <span className="text-xs text-[#666] font-semibold">{profile.designations}</span>}
                                </div>
                                {(profile.job_title || profile.company) && (
                                    <p className="text-sm text-[#444] font-medium mb-1">
                                        {profile.job_title}{profile.job_title && profile.company ? " · " : ""}{profile.company}
                                    </p>
                                )}
                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[#666] mt-2">
                                    <span className="flex items-center gap-1"><span className="material-symbols-outlined text-[13px]">mail</span>{profile.email}</span>
                                    {profile.phone && <span className="flex items-center gap-1"><span className="material-symbols-outlined text-[13px]">phone</span>{profile.phone}</span>}
                                    {(profile.city || profile.country) && (
                                        <span className="flex items-center gap-1"><span className="material-symbols-outlined text-[13px]">location_on</span>{[profile.city, profile.country].filter(Boolean).join(", ")}</span>
                                    )}
                                    {profile.member_since && (
                                        <span className="flex items-center gap-1"><span className="material-symbols-outlined text-[13px]">calendar_today</span>Member since {profile.member_since}</span>
                                    )}
                                    {profile.linkedin && (
                                        <a href={profile.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#007a8a] hover:underline">
                                            <span className="material-symbols-outlined text-[13px]">open_in_new</span>LinkedIn
                                        </a>
                                    )}
                                </div>
                            </div>

                            {/* Journey badges */}
                            <div className="flex flex-col gap-2 items-start sm:items-end flex-shrink-0">
                                {profile.program_journey && (
                                    <div className="text-right">
                                        <p className="text-[9px] font-black uppercase tracking-widest text-[#999] mb-0.5">Program Journey</p>
                                        <span className="text-[11px] font-bold bg-[#003d45]/10 text-[#003d45] px-2.5 py-0.5 rounded-full">{profile.program_journey}</span>
                                    </div>
                                )}
                                {profile.membership_journey && (
                                    <div className="text-right">
                                        <p className="text-[9px] font-black uppercase tracking-widest text-[#999] mb-0.5">Membership Journey</p>
                                        <span className="text-[11px] font-bold bg-[#007a8a]/10 text-[#007a8a] px-2.5 py-0.5 rounded-full">{profile.membership_journey}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Bio */}
                        {profile.bio && (
                            <div className="mt-4 pt-4 border-t border-[#f0f0f0]">
                                <p className="text-sm text-[#555] leading-relaxed">{profile.bio}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* My Groups */}
            {myGroups.length > 0 && (
                <div className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-[#e2e8f0] flex items-center gap-2">
                        <span className="material-symbols-outlined text-[#003d45] text-xl">group_work</span>
                        <h3 className="font-black text-[#003d45] uppercase tracking-wider text-sm">My Groups</h3>
                        <span className="ml-auto text-[10px] font-bold text-[#999]">{myGroups.length} group{myGroups.length !== 1 ? "s" : ""}</span>
                    </div>
                    <div className="flex flex-wrap gap-3 p-5">
                        {myGroups.map(g => {
                            const gname = g.name || g.entity_name || `${g.entity_type} ${g.entity_id}`;
                            return (
                                <Link key={`${g.entity_type}-${g.entity_id}`} href={groupUrl(g.entity_type, g.entity_id)}
                                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#e2e8f0] bg-[#f9fafa] hover:border-[#007a8a] hover:bg-[#e8f4f5] transition-all group">
                                    <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${TYPE_COLOR[g.entity_type] || "bg-slate-200 text-slate-600"}`}>
                                        {g.entity_type}
                                    </span>
                                    <span className="text-sm font-semibold text-[#003d45] group-hover:text-[#007a8a] transition-colors">{gname}</span>
                                    {g.role && <span className="text-[10px] text-[#999] font-bold">· {g.role}</span>}
                                </Link>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Recent Activity */}
            <div className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-[#e2e8f0] flex items-center gap-2">
                    <span className="material-symbols-outlined text-[#003d45] text-xl">history</span>
                    <h3 className="font-black text-[#003d45] uppercase tracking-wider text-sm">Recent Activities</h3>
                </div>
                {recent.length === 0 ? (
                    <div className="px-6 py-10 text-center text-[#999] text-sm">No activities found.</div>
                ) : (
                    <ul>
                        {recent.map((m, i) => (
                            <li key={`${m.id}-${i}`} className={`flex items-center gap-4 px-6 py-3.5 ${i < recent.length - 1 ? "border-b border-[#f0f0f0]" : ""} hover:bg-[#f9f9f9] transition-colors`}>
                                <div className="w-2 h-2 rounded-full bg-[#007a8a] flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-sm text-[#1a1a1a] truncate">{m.title}</p>
                                    <p className="text-xs text-[#666] mt-0.5 capitalize">{m.entity_name || m.entity_type} · {m.start_at ? formatEventDate(m.start_at, m.meeting_timezone, { month: "numeric", day: "numeric", year: "numeric" }) : "No date"}</p>
                                </div>
                                <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full flex-shrink-0 ${STATUS_COLOR[m.status] || "bg-slate-100 text-slate-500"}`}>
                                    {m.status.replace(/_/g, " ")}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {isAuthorizedForInterest && (
                <div className="space-y-8">
                    <InterestSubmissionsView />
                </div>
            )}
        </div>
    );
}
